/**
 * Line diff, for reviewing a prompt release.
 *
 * Reviewing a wording change by reading two 40-line instructions side by side is
 * how a deleted guardrail gets approved: the eye confirms that both look like the
 * same document. The diff makes the deletion the thing on screen.
 *
 * Classic Myers-style LCS over whole lines, in the simple O(n·m) table form. A
 * prompt is tens of lines, so the table is thousands of cells - nothing worth
 * optimising, and the linear-space variant is the version nobody can check by
 * reading. Word-level diffing is deliberately absent: what matters here is
 * whether a RULE is gone, and rules are lines.
 */

export type DiffOp = 'same' | 'add' | 'remove';

export interface DiffLine {
  op: DiffOp;
  text: string;
}

export function diffLines(before: string, after: string): DiffLine[] {
  /*
   * An empty document is zero lines, not one blank one. `''.split('\n')` gives
   * `['']`, which would make a first release read as "removed one blank line,
   * added everything" - noise at exactly the moment the diff is most read.
   */
  const lines = (text: string) => (text === '' ? [] : text.split('\n'));
  const a = lines(before);
  const b = lines(after);

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ op: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      // A removal before an addition where both are possible, so a replaced line
      // reads as "this went, that came" in the order a reviewer expects.
      out.push({ op: 'remove', text: a[i]! });
      i++;
    } else {
      out.push({ op: 'add', text: b[j]! });
      j++;
    }
  }
  while (i < a.length) out.push({ op: 'remove', text: a[i++]! });
  while (j < b.length) out.push({ op: 'add', text: b[j++]! });
  return out;
}

/** How much changed, for a one-line summary above the diff. */
export const diffCounts = (lines: DiffLine[]): { added: number; removed: number } => ({
  added: lines.filter((line) => line.op === 'add').length,
  removed: lines.filter((line) => line.op === 'remove').length,
});
